/* eslint-disable react/function-component-definition */
import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowRight, faArrowLeft, faExclamationTriangle } from '@fortawesome/fontawesome-free-solid';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Badge, Image, Alert } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';
import axios from 'axios';
import styles from './directMergeModal.module.scss';
import { TableContext } from '../../contexts/tableContext';
import { Entry } from '../../redux/entrySlice';
import SelectBox from '../../components/SelectBox/SelectBox';
import TextArea from '../../components/TextArea/TextArea';
import FieldResolutionForm from '../../components/FieldResolutionForm/FieldResolutionForm';
import { REACT_APP_API_CAFES_DROPDOWN } from '../../redux/apiConstants';
import Loader from '../../components/Loader/Loader';

const DirectMergeModal = React.memo(({ show, handleClose, onSuccess }) => {
  const { formthemeStyle, errormsgStyle } = useContext(TableContext);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [cafes, setCafes] = useState([]);
  const [loadingCafes, setLoadingCafes] = useState(false);
  const [selectedSourceCafe, setSelectedSourceCafe] = useState(null);
  const [selectedTargetCafe, setSelectedTargetCafe] = useState(null);
  const [fieldResolutions, setFieldResolutions] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    getValues,
  } = useForm();

  useEffect(() => {
    if (show) {
      getCafes();
      setStep(1);
      setSelectedSourceCafe(null);
      setSelectedTargetCafe(null);
      setFieldResolutions({});
      reset();
    }
  }, [show]);

  const getCafes = async () => {
    setLoadingCafes(true);
    try {
      const response = await axios.get(REACT_APP_API_CAFES_DROPDOWN);
      setCafes(response.data?.data?.cafes?.selectdata || []);
    } catch (err) {
      console.error('Error fetching cafes:', err);
      toast.error('Failed to load cafes');
    } finally {
      setLoadingCafes(false);
    }
  };

  const fetchCafeDetails = async (cafeId, type) => {
    try {
      const actionData = {
        actionUrl: `cafe/${cafeId}`,
        actionMethod: 'get',
      };
      const resp = await dispatch(Entry(actionData));
      if (resp.payload?.data?.cafe) {
        if (type === 'source') {
          setSelectedSourceCafe(resp.payload.data.cafe);
        } else {
          setSelectedTargetCafe(resp.payload.data.cafe);
        }
      }
    } catch (err) {
      console.error('Error fetching cafe details:', err);
    }
  };

  const watchSourceCafe = watch('sourceCafe');
  const watchTargetCafe = watch('targetCafe');

  useEffect(() => {
    if (watchSourceCafe) {
      const cafeId = watchSourceCafe.id || watchSourceCafe;
      if (cafeId && cafeId !== selectedSourceCafe?._id) {
        fetchCafeDetails(cafeId, 'source');
      }
    }
  }, [watchSourceCafe]);

  useEffect(() => {
    if (watchTargetCafe) {
      const cafeId = watchTargetCafe.id || watchTargetCafe;
      if (cafeId && cafeId !== selectedTargetCafe?._id) {
        fetchCafeDetails(cafeId, 'target');
      }
    }
  }, [watchTargetCafe]);

  const handleNextStep = () => {
    const values = getValues();
    const sourceCafeId = values.sourceCafe?.id || values.sourceCafe;
    const targetCafeId = values.targetCafe?.id || values.targetCafe;

    if (step === 1) {
      // Validation for step 1
      if (!sourceCafeId || !targetCafeId) {
        toast.error('Please select both source and target cafes');
        return;
      }

      if (sourceCafeId === targetCafeId) {
        toast.error('Source and target cafe cannot be the same');
        return;
      }

      // Check if cafes are already merged
      if (selectedSourceCafe?.mergedInto) {
        toast.error('Source cafe has already been merged');
        return;
      }

      setStep(2);
    } else if (step === 2) {
      // Validation for step 2 - ensure all fields have selections
      const requiredFields = ['name', 'description', 'address', 'location_name', 'images', 'timings'];
      const missingFields = requiredFields.filter(field => !fieldResolutions[field]);
      
      if (missingFields.length > 0) {
        toast.error(`Please select values for all fields`);
        return;
      }

      setStep(3);
    }
  };

  const handlePrevStep = () => {
    setStep(step - 1);
  };

  const handleFinalConfirm = () => {
    confirmAlert({
      title: '⚠️ WARNING: Irreversible Action',
      message: `You are about to permanently merge "${selectedSourceCafe?.name}" into "${selectedTargetCafe?.name}". The source cafe will be deleted and all its data will be transferred. This action CANNOT be undone. Are you absolutely sure?`,
      buttons: [
        {
          label: 'Yes, Merge Now',
          onClick: () => executeMerge(),
        },
        {
          label: 'Cancel',
        },
      ],
    });
  };

  const executeMerge = async () => {
    setSubmitting(true);
    setError(null);

    const values = getValues();
    const sourceCafeId = values.sourceCafe?.id || values.sourceCafe;
    const targetCafeId = values.targetCafe?.id || values.targetCafe;

    const apiData = {
      sourceCafeId: sourceCafeId,
      targetCafeId: targetCafeId,
      fieldResolutions: fieldResolutions,
      notes: values.notes || 'Direct merge by admin',
    };

    const actionData = {
      actionUrl: 'cafe/merge',
      actionMethod: 'post',
      apiData: apiData,
    };

    dispatch(Entry(actionData)).then((resp) => {
      setSubmitting(false);
      // Accept both 200 and 201 as success codes
      if (resp.payload.code === 200 || resp.payload.code === 201 || resp.payload.success === true) {
        const transferStats = resp.payload.data?.mergeResult?.transferStats;
        toast.success('Cafes merged successfully!');
        if (transferStats) {
          toast.info(
            `Transferred: ${transferStats.ordersTransferred || 0} orders, ${transferStats.drinksTransferred || 0} drinks, ${transferStats.employeesTransferred || 0} employees`
          );
        }
        handleClose();
        if (onSuccess) onSuccess();
        // Navigate to target cafe
        setTimeout(() => {
          navigate(`/cafe/viewdetails/${targetCafeId}`);
        }, 2000);
      } else if (resp.payload.code === 400) {
        toast.error(resp.payload.message || 'Invalid merge request');
        setError(resp.payload.message);
      } else {
        toast.error('Failed to merge cafes');
        setError(resp.payload.message);
      }
    });
  };

  const renderCafePreview = (cafe, type) => {
    if (!cafe) return null;

    return (
      <Card className={styles.cafePreviewCard}>
        <Card.Body>
          <Row>
            <Col xs={4} className={styles.imageCol}>
              {cafe.image ? (
                <Image src={cafe.image} alt={cafe.name} className={styles.previewImage} fluid rounded />
              ) : (
                <div className={styles.placeholderImage}>
                  <span>{cafe.name?.charAt(0) || '?'}</span>
                </div>
              )}
            </Col>
            <Col xs={8}>
              <h6 className={styles.previewCafeName}>{cafe.name}</h6>
              <p className={styles.previewAddress}>{cafe.address || 'No address'}</p>
              <div className={styles.previewBadges}>
                <Badge bg={type === 'source' ? 'danger' : 'success'}>
                  {type === 'source' ? 'Will be deleted' : 'Will remain'}
                </Badge>
                <Badge bg={cafe.isClaimed ? 'info' : 'warning'}>{cafe.cafeStatus}</Badge>
                {cafe.vendor && <Badge bg="secondary">Vendor: {cafe.vendor.name}</Badge>}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  if (!show) return null;

  return (
    <div className={styles.popupbox}>
      <div className={styles.box}>
        <button className={styles.closeicon} aria-label="Close" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <h2 className={styles.heading}>Direct Merge Cafes</h2>
        
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepItem} ${step >= 1 ? styles.active : ''}`}>
            <div className={styles.stepNumber}>1</div>
            <div className={styles.stepLabel}>Select Cafes</div>
          </div>
          <div className={styles.stepLine}></div>
          <div className={`${styles.stepItem} ${step >= 2 ? styles.active : ''}`}>
            <div className={styles.stepNumber}>2</div>
            <div className={styles.stepLabel}>Resolve Fields</div>
          </div>
          <div className={styles.stepLine}></div>
          <div className={`${styles.stepItem} ${step >= 3 ? styles.active : ''}`}>
            <div className={styles.stepNumber}>3</div>
            <div className={styles.stepLabel}>Confirm</div>
          </div>
        </div>

        <div className="container-fluid mt-4">
          {loadingCafes ? (
            <div className={styles.loadingContainer}>
              <Loader />
              <p>Loading cafes...</p>
            </div>
          ) : (
            <form>
              {/* Step 1: Cafe Selection */}
              {step === 1 && (
                <div className={styles.stepContent}>
                  <Alert variant="warning" className={styles.warningAlert}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>Warning:</strong> This is a permanent action. The source cafe will be deleted and cannot be recovered.
                  </Alert>

                  <Row>
                    <Col lg={12} md={12} sm={12}>
                      <SelectBox
                        className={styles.inputbox}
                        classNamedark={styles.inputbox1}
                        name="sourceCafe"
                        label="Source Cafe (Will be deleted)"
                        labelstyle={styles.formlabel}
                        control={control}
                        register={register}
                        values={cafes}
                        errors={errors}
                        placeholder="Select source cafe..."
                        mandatory
                        isRequired
                      />
                    </Col>
                  </Row>

                  {selectedSourceCafe && (
                    <Row>
                      <Col lg={12}>
                        <label className={styles.previewLabel}>Source Cafe Preview:</label>
                        {renderCafePreview(selectedSourceCafe, 'source')}
                      </Col>
                    </Row>
                  )}

                  <Row className="mt-3">
                    <Col lg={12} md={12} sm={12}>
                      <SelectBox
                        className={styles.inputbox}
                        classNamedark={styles.inputbox1}
                        name="targetCafe"
                        label="Target Cafe (Will remain)"
                        labelstyle={styles.formlabel}
                        control={control}
                        register={register}
                        values={cafes}
                        errors={errors}
                        placeholder="Select target cafe..."
                        mandatory
                        isRequired
                      />
                    </Col>
                  </Row>

                  {selectedTargetCafe && (
                    <Row>
                      <Col lg={12}>
                        <label className={styles.previewLabel}>Target Cafe Preview:</label>
                        {renderCafePreview(selectedTargetCafe, 'target')}
                      </Col>
                    </Row>
                  )}
                </div>
              )}

              {/* Step 2: Field Resolution */}
              {step === 2 && (
                <div className={styles.stepContent}>
                  <FieldResolutionForm
                    sourceCafe={selectedSourceCafe}
                    targetCafe={selectedTargetCafe}
                    value={fieldResolutions}
                    onChange={setFieldResolutions}
                  />
                </div>
              )}

              {/* Step 3: Confirmation */}
              {step === 3 && (
                <div className={styles.stepContent}>
                  <Alert variant="danger" className={styles.dangerAlert}>
                    <h5>
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                      Final Confirmation Required
                    </h5>
                    <p>Please review the merge details carefully before proceeding:</p>
                  </Alert>

                  <div className={styles.confirmationSummary}>
                    <Row>
                      <Col md={6}>
                        <div className={styles.summarySection}>
                          <h6 className={styles.summaryTitle}>
                            <Badge bg="danger">Source Cafe (Will be deleted)</Badge>
                          </h6>
                          {renderCafePreview(selectedSourceCafe, 'source')}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className={styles.summarySection}>
                          <h6 className={styles.summaryTitle}>
                            <Badge bg="success">Target Cafe (Will remain)</Badge>
                          </h6>
                          {renderCafePreview(selectedTargetCafe, 'target')}
                        </div>
                      </Col>
                    </Row>

                    <div className={styles.fieldSelectionSummary}>
                      <h6>Field Selections:</h6>
                      <div className={styles.selectionGrid}>
                        <div><strong>From Source:</strong> {Object.values(fieldResolutions).filter(v => v === 'source').length} fields</div>
                        <div><strong>From Target:</strong> {Object.values(fieldResolutions).filter(v => v === 'target').length} fields</div>
                      </div>
                    </div>

                    <Row className="mt-3">
                      <Col lg={12}>
                        <TextArea
                          className={styles.inputbox}
                          classNamedark={styles.inputbox1}
                          labelstyle={styles.formlabel}
                          label="Notes (Optional)"
                          fieldName="notes"
                          placeHolder="Add any notes about this merge..."
                          register={register}
                          errors={errors}
                          maximLength={500}
                          rows={3}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className={styles.navigationButtons}>
                <div>
                  {step > 1 && (
                    <button
                      type="button"
                      className={styles.resetbtn}
                      onClick={handlePrevStep}
                      disabled={submitting}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                      Previous
                    </button>
                  )}
                </div>
                <div className={styles.rightButtons}>
                  {step < 3 ? (
                    <button
                      type="button"
                      className={styles.formbtn}
                      onClick={handleNextStep}
                    >
                      Next
                      <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={styles.mergeBtnDanger}
                      onClick={handleFinalConfirm}
                      disabled={submitting}
                    >
                      {submitting ? 'Merging...' : 'Merge Cafes'}
                    </button>
                  )}
                </div>
              </div>

              {error && (
                <div className={styles.errorMessage}>
                  {error}
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
});

export default DirectMergeModal;
